import "react-loading-skeleton/dist/skeleton.css";

import { useMemo } from "react";
import Skeleton from "react-loading-skeleton";
import ReactMarkdown, { Components, Options } from "react-markdown";
import { ReactMarkdownProps } from "react-markdown/lib/complex-types";
import { PluggableList } from "react-markdown/lib/react-markdown";
import remarkGfm from "remark-v3";

import { cn } from "@src/shared/lib/utils";
import { usePatientCitations } from "@src/shared/providers/citationsProviders";

import { loadingHandler, remarkLoading } from "./plugins/loading";
import { Sup } from "./Sup";

type MarkdownComponents = Components & {
  loading?: (props: ReactMarkdownProps) => React.ReactNode;
};

export interface MarkdownRendererProps extends Omit<Options, "children"> {
  /** The markdown text to render */
  markdown: string;

  /** Optional CSS class name to apply to the root container */
  className?: string;

  /** Optional custom markdown components to merge and override defaults */
  components?: Components;

  /** Optional remark plugins to use for markdown processing */
  remarkPlugins?: PluggableList;
}

/**
 * Base markdown components that produce tailwind-prose styling with custom
 * slots (Sup, Skeleton, Headers) wired to shadcn primitives.
 */
const defaultMarkdownComponents: MarkdownComponents = {
  sup: Sup,
  section: ({ node, children, ...props }) => {
    const isFootnotes = node?.properties?.dataFootnotes !== undefined;
    if (isFootnotes) {
      return null;
    }
    return <section {...props}>{children}</section>;
  },
  loading: ({ node }) => (
    <Skeleton
      height={Number(node?.properties?.height) || 20}
      width={Number(node?.properties?.width) ?? undefined}
    />
  ),
};

/**
 * Markdown renderer that produces nice typographic output with citation
 * footnotes and inline loading skeletons. Uses Tailwind's `prose` utilities
 * from @tailwindcss/typography for baseline styling.
 */
export function MarkdownRenderer({
  markdown = "",
  className,
  components,
  remarkPlugins,
}: MarkdownRendererProps): JSX.Element | null {
  const { citations } = usePatientCitations();

  const footnotes = useMemo(() => {
    return citations
      .map((citation) => `[^${citation.marker}]: ${citation.value}`)
      .join("\n");
  }, [citations]);

  const finalComponents = useMemo(() => {
    return { ...defaultMarkdownComponents, ...components };
  }, [components]);

  return (
    <div
      className={cn(
        "prose prose-sm max-w-none text-foreground",
        "prose-headings:font-semibold prose-headings:text-foreground prose-headings:tracking-tight",
        "prose-p:my-2 prose-p:leading-relaxed",
        "prose-strong:text-foreground prose-em:text-foreground",
        "prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5",
        "prose-a:text-accent prose-a:no-underline hover:prose-a:underline",
        className,
      )}
    >
      <ReactMarkdown
        components={finalComponents}
        remarkPlugins={[remarkGfm, remarkLoading, ...(remarkPlugins ?? [])]}
        remarkRehypeOptions={{
          handlers: {
            loading: loadingHandler,
          },
        }}
      >
        {markdown + "\n" + footnotes}
      </ReactMarkdown>
    </div>
  );
}

export default MarkdownRenderer;
